import { Button } from "@shared-ui/button";
import { Label } from "@shared-ui/label";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import {
	type AdminCategoryRow,
	previewCategoryMove,
	updateAdminCategory,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Move a category to another parent, having seen what it costs.
 *
 * THIS FILE RESTATES NO RULE, and that is the point of the unit. Whether a move
 * is legal -- cycles, the depth cap, the leaf-only template rule -- is decided by
 * one predicate on the server that the save calls too. A second copy of the
 * depth arithmetic here would drift the first time the cap changed, and the
 * screen would then confidently permit a move the save refuses.
 *
 * So the dialog asks, renders the answer, and enables Move only when the answer
 * has no problems in it. Every sentence an operator reads on a refusal is the
 * server's own.
 *
 * Re-parenting is not offered by `CategoryDialog`, deliberately: moving a
 * subtree changes the depth of everything beneath it and can strand a spec
 * template on a node that stops being a leaf. That is what this screen is for.
 */

const TITLE_ID = "move-category-dialog-title";

interface MoveCategoryDialogProps {
	category: AdminCategoryRow;
	onClose: () => void;
	onMoved: () => Promise<void> | void;
}

export function MoveCategoryDialog({
	category,
	onClose,
	onMoved,
}: MoveCategoryDialogProps) {
	/*
	 * `undefined` means "nothing chosen yet"; `null` means "top level", which is
	 * a real destination. Collapsing the two would make the dialog unable to tell
	 * an untouched form from a promotion.
	 */
	const [destination, setDestination] = useState<
		{ id: string; name: string } | null | undefined
	>(undefined);

	const chosen = destination !== undefined;
	const parentId = destination?.id ?? null;

	const preview = useQuery({
		queryKey: ["admin-catalog-move-preview", category.id, parentId],
		queryFn: () => previewCategoryMove(category.id, parentId),
		enabled: chosen,
	});

	const move = useMutation({
		mutationFn: () => updateAdminCategory(category.id, { parentId }),
		onSuccess: async () => {
			await onMoved();
			onClose();
		},
	});

	const problems = preview.data?.problems ?? [];
	const allowed = preview.isSuccess && problems.length === 0;

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!move.isPending) onClose();
			}}
			className="grid max-h-[85vh] w-full max-w-xl gap-4 overflow-y-auto rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Move {category.name}
				</h2>
				<p className="text-sm text-muted-foreground">
					Everything filed under this category moves with it, and its depth
					changes to match its new parent.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="move-destination">New parent</Label>
				{/*
				 * The existing picker, which exists because "the taxonomy has 674 leaf
				 * categories, and a 674-row dropdown is not a control anyone can use".
				 * Still `clearable`, so a chosen destination can be undone; promoting
				 * to the top level is the checkbox below rather than this, for the
				 * reason stated there.
				 */}
				<CategoryPicker
					id="move-destination"
					label="New parent"
					value={destination?.id ?? null}
					valueLabel={destination?.name ?? null}
					clearable
					disabled={chosen && destination === null}
					onChange={(next) => setDestination(next)}
				/>
			</div>

			{/*
			 * TOP LEVEL IS ITS OWN CONTROL, not "clear the picker".
			 *
			 * `CategoryPicker` renders its Clear affordance only once a value is
			 * set, so promoting a category would have meant choosing some unrelated
			 * parent and then clearing it -- a sequence nobody would guess, and one
			 * an operator moving a category that is ALREADY top level cannot
			 * perform at all.
			 */}
			<label className="flex items-center gap-2 text-sm">
				<input
					type="checkbox"
					checked={chosen && destination === null}
					onChange={(event) =>
						setDestination(event.target.checked ? null : undefined)
					}
				/>
				Move it to the top level instead
			</label>

			{preview.isPending && chosen ? (
				<p className="text-sm text-muted-foreground">Checking that move…</p>
			) : null}

			{preview.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(preview.error, "Could not check that move.")}
				</p>
			) : null}

			{preview.isSuccess ? (
				<div className="grid gap-3">
					<p className="rounded-2xl border bg-slate-50 p-3 text-sm">
						{preview.data.descendants === 0
							? "Nothing is filed under this category, so it moves on its own."
							: preview.data.descendants === 1
								? "1 subcategory moves with it."
								: `${preview.data.descendants} subcategories move with it.`}
					</p>

					{problems.length > 0 ? (
						/*
						 * EVERY problem, in the server's words. The predicate reports all
						 * of them rather than the first, so an operator fixing a depth
						 * breach is not then refused again for a stranded template.
						 */
						<div
							role="alert"
							className="grid gap-2 rounded-2xl border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900"
						>
							<p className="font-medium">
								{problems.length === 1
									? "This move is not allowed"
									: `This move breaks ${problems.length} rules`}
							</p>
							<ul className="list-inside list-disc">
								{problems.map((problem) => (
									<li key={problem.rule}>{problem.message}</li>
								))}
							</ul>
						</div>
					) : null}
				</div>
			) : null}

			{move.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(move.error, "Could not move this category.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={move.isPending}>
					Cancel
				</Button>
				<Button
					onClick={() => move.mutate()}
					disabled={!allowed || move.isPending}
				>
					{move.isPending ? "Moving…" : "Move"}
				</Button>
			</div>
		</Dialog>
	);
}
